import React, { useState, useEffect } from 'react';
import { Calendar, Clock, School, AlertCircle, ExternalLink, Filter } from 'lucide-react';
import { getDeadlines, getDeadlineStats } from '../services/deadlineService';
import { parseLocalDate } from '../utils/dateUtils';

/**
 * Main page component for displaying school deadlines
 */
export default function DeadlinesPage() {
    const [deadlines, setDeadlines] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({
        futureOnly: true,
        eventType: '',
        searchQuery: ''
    });

    useEffect(() => {
        loadDeadlines();
        loadStats();
    }, [filter.futureOnly, filter.eventType]);

    const loadDeadlines = async () => {
        setLoading(true);
        try {
            const data = await getDeadlines({
                futureOnly: filter.futureOnly,
                eventType: filter.eventType || undefined,
                limit: 50
            });
            setDeadlines(data);
        } catch (error) {
            console.error('Error loading deadlines:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const statsData = await getDeadlineStats();
            setStats(statsData);
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const filteredDeadlines = deadlines.filter(deadline => {
        if (!filter.searchQuery) return true;
        const query = filter.searchQuery.toLowerCase();
        return (
            deadline.school_name?.toLowerCase().includes(query) ||
            deadline.article_title?.toLowerCase().includes(query) ||
            deadline.event_type?.toLowerCase().includes(query)
        );
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">
                        📅 Calendrier des Admissions
                    </h1>
                    <p className="text-lg text-gray-600">
                        Ne ratez aucune échéance importante pour vos candidatures
                    </p>
                </div>

                {/* Statistics Cards */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                        <StatCard
                            icon={<Calendar className="w-6 h-6 text-blue-600" />}
                            label="Total"
                            value={stats.total}
                            color="blue"
                        />
                        <StatCard
                            icon={<Clock className="w-6 h-6 text-green-600" />}
                            label="À venir"
                            value={stats.future}
                            color="green"
                        />
                        <StatCard
                            icon={<School className="w-6 h-6 text-purple-600" />}
                            label="Établissements"
                            value={stats.schools}
                            color="purple"
                        />
                        <StatCard
                            icon={<AlertCircle className="w-6 h-6 text-orange-600" />}
                            label="Types d'événements"
                            value={stats.eventTypes}
                            color="orange"
                        />
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter className="w-5 h-5 text-gray-600" />
                        <h2 className="text-lg font-semibold text-gray-900">Filtres</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Search */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rechercher
                            </label>
                            <input
                                type="text"
                                placeholder="École, événement..."
                                value={filter.searchQuery}
                                onChange={(e) => setFilter({ ...filter, searchQuery: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Event Type Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type d'événement
                            </label>
                            <select
                                value={filter.eventType}
                                onChange={(e) => setFilter({ ...filter, eventType: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">Tous les types</option>
                                <option value="concours">Concours</option>
                                <option value="bourse">Bourses</option>
                                <option value="inscription">Inscription</option>
                                <option value="formation">Formation</option>
                                <option value="résultats">Résultats</option>
                            </select>
                        </div>

                        {/* Future Only Toggle */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Affichage
                            </label>
                            <button
                                onClick={() => setFilter({ ...filter, futureOnly: !filter.futureOnly })}
                                className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${filter.futureOnly
                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                {filter.futureOnly ? 'Futures échéances' : 'Toutes les échéances'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Deadlines List */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredDeadlines.length === 0 ? (
                            <div className="bg-white rounded-lg shadow-md p-12 text-center">
                                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                    Aucune échéance trouvée
                                </h3>
                                <p className="text-gray-600">
                                    Essayez de modifier vos filtres ou revenez plus tard.
                                </p>
                            </div>
                        ) : (
                            filteredDeadlines.map((deadline) => (
                                <DeadlineCard key={deadline.id} deadline={deadline} />
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Stat Card Component
 */
function StatCard({ icon, label, value, color }) {
    const colorClasses = {
        blue: 'from-blue-50 to-blue-100 border-blue-200',
        green: 'from-green-50 to-green-100 border-green-200',
        purple: 'from-purple-50 to-purple-100 border-purple-200',
        orange: 'from-orange-50 to-orange-100 border-orange-200'
    };

    return (
        <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-lg p-6`}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">{label}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
                </div>
                <div>{icon}</div>
            </div>
        </div>
    );
}

/**
 * Individual Deadline Card Component
 */
function DeadlineCard({ deadline }) {
    const daysUntil = deadline.deadline_date
        ? Math.ceil((parseLocalDate(deadline.deadline_date) - new Date()) / (1000 * 60 * 60 * 24))
        : null;

    const getUrgencyColor = () => {
        if (daysUntil === null) return 'border-gray-300 bg-white';
        if (daysUntil < 0) return 'border-red-300 bg-red-50';
        if (daysUntil <= 7) return 'border-red-400 bg-red-50';
        if (daysUntil <= 30) return 'border-orange-400 bg-orange-50';
        return 'border-green-400 bg-green-50';
    };

    const getUrgencyBadge = () => {
        if (daysUntil === null) return null;
        if (daysUntil < 0) return { text: '❌ Expiré', color: 'bg-red-600 text-white' };
        if (daysUntil === 0) return { text: '🔴 Aujourd\'hui', color: 'bg-red-600 text-white' };
        if (daysUntil === 1) return { text: '🔴 Demain', color: 'bg-red-600 text-white' };
        if (daysUntil <= 7) return { text: `🔴 ${daysUntil}j`, color: 'bg-red-500 text-white' };
        if (daysUntil <= 30) return { text: `⚠️ ${daysUntil}j`, color: 'bg-orange-500 text-white' };
        return { text: `✅ ${daysUntil}j`, color: 'bg-green-500 text-white' };
    };

    const eventTypeColors = {
        concours: 'bg-blue-100 text-blue-800',
        bourse: 'bg-purple-100 text-purple-800',
        inscription: 'bg-orange-100 text-orange-800',
        formation: 'bg-green-100 text-green-800',
        résultats: 'bg-gray-100 text-gray-800',
        information: 'bg-cyan-100 text-cyan-800',
        orientation: 'bg-pink-100 text-pink-800'
    };

    const urgencyBadge = getUrgencyBadge();

    return (
        <div className={`border-l-4 ${getUrgencyColor()} rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow`}>
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{deadline.school_name}</h3>
                        {urgencyBadge && (
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${urgencyBadge.color}`}>
                                {urgencyBadge.text}
                            </span>
                        )}
                    </div>

                    <p className="text-gray-700 mb-3">{deadline.article_title}</p>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        {deadline.deadline_date && (
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span className="font-medium">
                                    {parseLocalDate(deadline.deadline_date).toLocaleDateString('fr-FR', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                </span>
                            </div>
                        )}

                        {deadline.event_type && (
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${eventTypeColors[deadline.event_type] || eventTypeColors.information}`}>
                                {deadline.event_type}
                            </span>
                        )}

                        <span className="text-xs text-gray-500">
                            Source: {deadline.source}
                        </span>
                    </div>
                </div>

                {deadline.article_url && (
                    <a
                        href={deadline.article_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                    >
                        <span>Voir l'article</span>
                        <ExternalLink className="w-4 h-4" />
                    </a>
                )}
            </div>
        </div>
    );
}
